/**
 * TabellaMensileWidget — RD4 config-driven + v108 Backfill retroattivo.
 *
 * Legge `columns` da `sezione.campo.config.columns`. Renderizza dinamicamente le
 * colonne in base al `tipo`:
 *   - readonly_label → nome mese (system)
 *   - number → input number con min/max/step
 *   - enum   → select
 *   - text   → input text
 *   - bool   → checkbox
 *   - date   → input date
 *   - readonly_computed → non editabile (renderizzato solo se popolato)
 *
 * KPI YTD calcolati solo per colonne con `aggregate="sum"|"avg"` e
 * `target_key_hotel` popolato.
 *
 * v108 — RM e SA possono editare retroattivamente i mesi passati (mese < meseCorrente)
 * che non hanno dati provenienti da sibling report già inviati. Le righe modificate
 * retroattivamente ricevono flag `backfill=true` lato backend e badge "retroattivo".
 */
import { Info } from "lucide-react";
import { MONTHS_FULL, kpiCard, fmtNumber } from "./widgetHelpers";

// Colonne di fallback quando la config non è ancora migrata (RD2 default).
const FALLBACK_COLUMNS = [
  { key: "mese", label: "Mese", tipo: "readonly_label", ordine: 1, system: true },
  { key: "fatturato_mese", label: "Fatturato €", tipo: "number", ordine: 2, step: 0.01, target_key_hotel: "fatturato_annuale", aggregate: "sum", unit: " €", decimals: 0 },
  { key: "incasso_food", label: "Food €", tipo: "number", ordine: 3, step: 0.01, target_key_hotel: "food_annuale", aggregate: "sum", unit: " €", decimals: 0, sibling_path: ["breakfast", "incasso_food_mese"] },
  { key: "transfer_venduti", label: "Transfer", tipo: "number", ordine: 4, step: 1, target_key_hotel: "transfer_annuale", aggregate: "sum", sibling_path: ["transfer", "transfer_venduti_mese"] },
  { key: "booking_score", label: "Booking", tipo: "number", ordine: 5, step: 0.1, target_key_hotel: "booking_target", aggregate: "avg", decimals: 2, sibling_path: ["booking", "punteggio_attuale"] },
  { key: "pulizia_booking", label: "Pulizia Bkg", tipo: "number", ordine: 6, step: 0.1, target_key_hotel: "pulizia_booking_target", aggregate: "avg", decimals: 2, sibling_path: ["booking", "media_pulizia"] },
  { key: "google_score", label: "Google", tipo: "number", ordine: 7, step: 0.1, target_key_hotel: "google_target", aggregate: "avg", decimals: 2, sibling_path: ["google", "punteggio_attuale"] },
  { key: "stato", label: "Stato", tipo: "enum", ordine: 8, system: true, enum_options: [
    { value: "non_compilato", label: "Non compilato" },
    { value: "compilato", label: "Compilato" },
    { value: "inviata", label: "Da relazione inviata" },
  ]},
];

function editableCols(cols) {
  return cols.filter((c) => c.tipo !== "readonly_label" && c.key !== "stato" && c.tipo !== "readonly_computed");
}

export function TabellaMensileWidget({ data, setData, isReadOnly, siblings, meseCorrente, targets, columns, userRole }) {
  const cfgCols = (columns && columns.length ? columns : FALLBACK_COLUMNS)
    .slice()
    .sort((a, b) => (a.ordine || 0) - (b.ordine || 0));
  const dataCols = editableCols(cfgCols);

  // v108: solo RM e SA possono fare backfill retroattivo.
  const canBackfill = !isReadOnly && (userRole === "resident_manager" || userRole === "super_admin");

  const emptyRow = () => {
    const r = { mese: 0, stato: "non_compilato" };
    dataCols.forEach((c) => { r[c.key] = null; });
    return r;
  };

  const righe = data?.righe && data.righe.length === 12
    ? data.righe
    : Array.from({ length: 12 }, (_, i) => ({ ...emptyRow(), mese: i + 1 }));

  // Sibling join: per colonne con sibling_path, popola dai report inviati.
  // v123 — Per colonne SENZA sibling_path (es. fatturato_mese) fallback al
  // valore dentro `sibling.sections.tabella_annuo.righe[idx][col.key]`.
  const siblingByMese = {};
  const siblingDocByMese = {};
  (siblings || []).forEach((s) => {
    if (s.stato !== "inviata") return;
    const src = s.sections || {};
    const annuoRighe = src?.tabella_annuo?.righe || [];
    const annuoRow = annuoRighe.find((rr) => rr?.mese === s.mese) || {};
    const row = {};
    dataCols.forEach((c) => {
      if (c.sibling_path && c.sibling_path.length === 2) {
        row[c.key] = src?.[c.sibling_path[0]]?.[c.sibling_path[1]] ?? null;
      } else {
        // v123 fix: fatturato_mese e simili → row-level nel sibling
        row[c.key] = annuoRow?.[c.key] ?? null;
      }
    });
    siblingByMese[s.mese] = row;
    siblingDocByMese[s.mese] = s;
  });

  // v123 — Un valore è "vuoto" e quindi sovrascrivibile se null/undefined/""/0
  const isEmptyAnnuoValue = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === "string" && v.trim() === "") return true;
    if (typeof v === "number" && v === 0) return true;
    return false;
  };

  const rowHasAnyData = (r) => dataCols.some((c) => {
    const v = r[c.key];
    if (c.tipo === "number") return v != null && v !== "" && !Number.isNaN(parseFloat(v));
    return v != null && v !== "";
  });

  const computeStato = (r) => rowHasAnyData(r) ? "compilato" : "non_compilato";

  const displayRows = righe.map((r, i) => {
    const m = i + 1;
    if (m === meseCorrente) {
      return { ...r, mese: m, stato: computeStato(r), isCurrent: true, isPast: false, isFuture: false };
    }
    if (m < meseCorrente) {
      const sib = siblingByMese[m];
      // Priorità: sibling (inviata) → read-only con eccezione v123 campi null
      if (sib) {
        const merged = { mese: m, isCurrent: false, isPast: true, isFuture: false, hasSibling: true };
        dataCols.forEach((c) => { merged[c.key] = sib[c.key] ?? null; });
        merged.stato = "inviata";
        merged._siblingReportId = siblingDocByMese[m]?.id || null;
        return merged;
      }
      const local = { mese: m, isCurrent: false, isPast: true, isFuture: false, hasSibling: false };
      dataCols.forEach((c) => { local[c.key] = r[c.key] ?? null; });
      local.backfill = !!r.backfill;
      local.stato = computeStato(local);
      return local;
    }
    return { mese: m, isFuture: true, isCurrent: false, isPast: false, stato: "non_compilato" };
  });

  const updateRow = (targetMese, colKey, colTipo, rawValue) => {
    if (isReadOnly) return;
    // v123 — Per righe sibling, l'edit viene mandato comunque: backend decide
    // di propagare al sibling (sibling-override) se il campo lì è null/empty.
    // La riga locale resta virgin lato backend (merge in GET).
    const displayRow = displayRows.find((dr) => dr.mese === targetMese);
    const isSibling = !!displayRow?.hasSibling;

    const next = righe.map((r, i) => {
      const m = i + 1;
      if (m !== targetMese) return r;
      let parsed = rawValue;
      if (colTipo === "number") {
        parsed = rawValue === "" ? null : parseFloat(rawValue);
        if (Number.isNaN(parsed)) parsed = null;
      } else if (colTipo === "bool") {
        parsed = !!rawValue;
      }
      if (isSibling) {
        // Costruiamo la riga da zero preservando metadati sibling: così il BE
        // riconosce la riga come "sibling-row" e applica override logic.
        const updated = {
          mese: m,
          stato: "inviata",
          _sibling_report_id: displayRow?._siblingReportId || null,
          [colKey]: parsed,
        };
        return updated;
      }
      const updated = { ...r, mese: m, [colKey]: parsed };
      updated.stato = computeStato(updated);
      // Marca intent di backfill su mesi passati (il backend conferma e persiste i metadati)
      if (m < meseCorrente && rowHasAnyData(updated)) {
        updated.backfill = true;
      } else if (m < meseCorrente && !rowHasAnyData(updated)) {
        delete updated.backfill;
      }
      return updated;
    });
    setData({ ...data, righe: next });
  };

  const aggregateUpTo = (col) => {
    const vals = [];
    for (let m = 1; m <= meseCorrente; m++) {
      const v = displayRows[m - 1]?.[col.key];
      if (v == null || v === "") continue;
      const n = parseFloat(v);
      if (!Number.isNaN(n)) vals.push(n);
    }
    if (vals.length === 0) return null;
    if (col.aggregate === "avg") return vals.reduce((a, b) => a + b, 0) / vals.length;
    return vals.reduce((a, b) => a + b, 0);
  };

  // Determina se una cella è editabile per il row/col specifici
  // v123 — Sibling-row cells: editabili se valore specifico è vuoto (null/0/"")
  const isCellEditable = (row, col) => {
    if (isReadOnly) return false;
    if (col.tipo === "readonly_computed") return false;
    if (col.key === "stato") return false;
    if (row.isFuture) return false;
    if (row.hasSibling) {
      if (!canBackfill) return false;
      // Solo campi mappabili su source sibling autoritativa
      const mapped = !!(col.sibling_path && col.sibling_path.length === 2) || col.key === "fatturato_mese";
      if (!mapped) return false;
      return isEmptyAnnuoValue(row[col.key]);
    }
    if (row.isCurrent) return true;
    if (row.isPast) return canBackfill;
    return false;
  };

  const renderCell = (row, col) => {
    if (col.tipo === "readonly_label") {
      return (
        <span className="font-medium whitespace-nowrap">
          {MONTHS_FULL[row.mese - 1] || row.mese}
          {row.isCurrent && <span className="ml-1.5 text-[9px] uppercase text-lavender-600 dark:text-lavender-300 font-semibold tracking-wider">corrente</span>}
          {row.isPast && !row.hasSibling && row.backfill && (
            <span className="ml-1.5 text-[9px] uppercase text-lavender-700 dark:text-lavender-300 font-semibold tracking-wider border border-lavender-400 dark:border-lavender-500/60 rounded px-1 py-[1px]">retroattivo</span>
          )}
        </span>
      );
    }
    if (col.key === "stato") {
      return <StatoBadge stato={row.stato} isFuture={row.isFuture} backfill={!!row.backfill && row.isPast && !row.hasSibling} />;
    }

    const editable = isCellEditable(row, col);
    const isSiblingOverride = editable && row.hasSibling;
    if (!editable) {
      const v = row[col.key];
      if (col.tipo === "enum") {
        const opt = (col.enum_options || []).find((o) => o.value === v);
        return <span className="text-muted-foreground">{opt?.label || v || "—"}</span>;
      }
      if (col.tipo === "bool") return <span className="text-muted-foreground">{v ? "Sì" : v === false ? "No" : "—"}</span>;
      if (col.tipo === "number") return <span className="tabular-nums text-muted-foreground">{fmtNumber(v)}</span>;
      return <span className="text-muted-foreground">{v ?? "—"}</span>;
    }

    const testId = `row-${row.mese}-${col.key}`;
    const siblingOverrideCls = isSiblingOverride
      ? "border-amber-400/70 dark:border-amber-500/50 bg-amber-50/40 dark:bg-amber-950/20"
      : "border-border/60";
    const siblingTitle = isSiblingOverride
      ? `Campo mancante nella relazione di ${MONTHS_FULL[row.mese - 1]}. Compilalo qui — verrà propagato alla relazione originale.`
      : undefined;
    if (col.tipo === "number") {
      return (
        <input
          type="number"
          step={col.step != null ? String(col.step) : "any"}
          min={col.min ?? undefined}
          max={col.max ?? undefined}
          value={row[col.key] ?? ""}
          onChange={(e) => updateRow(row.mese, col.key, "number", e.target.value)}
          title={siblingTitle}
          className={`w-24 bg-transparent border ${siblingOverrideCls} rounded px-1.5 py-1 tabular-nums focus:border-lavender-500 focus:ring-1 focus:ring-lavender-500/30 outline-none`}
          data-testid={isSiblingOverride ? `${testId}-sibling-override` : testId}
        />
      );
    }
    if (col.tipo === "text") {
      return (
        <input
          type="text"
          value={row[col.key] ?? ""}
          onChange={(e) => updateRow(row.mese, col.key, "text", e.target.value)}
          className="w-32 bg-transparent border border-border/60 rounded px-1.5 py-1 focus:border-lavender-500 focus:ring-1 focus:ring-lavender-500/30 outline-none"
          data-testid={testId}
        />
      );
    }
    if (col.tipo === "date") {
      return (
        <input
          type="date"
          value={row[col.key] ?? ""}
          onChange={(e) => updateRow(row.mese, col.key, "date", e.target.value)}
          className="bg-transparent border border-border/60 rounded px-1.5 py-1 focus:border-lavender-500 outline-none"
          data-testid={testId}
        />
      );
    }
    if (col.tipo === "bool") {
      return (
        <input
          type="checkbox"
          checked={!!row[col.key]}
          onChange={(e) => updateRow(row.mese, col.key, "bool", e.target.checked)}
          data-testid={testId}
        />
      );
    }
    if (col.tipo === "enum") {
      return (
        <select
          value={row[col.key] ?? ""}
          onChange={(e) => updateRow(row.mese, col.key, "enum", e.target.value)}
          className="bg-transparent border border-border/60 rounded px-1.5 py-1 text-xs focus:border-lavender-500 outline-none"
          data-testid={testId}
        >
          <option value="">—</option>
          {(col.enum_options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4" data-testid="widget-tabella-mensile">
      {canBackfill ? (
        <div
          className="flex items-start gap-2.5 rounded-xl border border-lavender-300/60 dark:border-lavender-500/40 bg-lavender-50/80 dark:bg-lavender-950/30 px-3.5 py-2.5"
          data-testid="backfill-info-banner"
        >
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-lavender-700 dark:text-lavender-300" />
          <div className="text-xs text-lavender-900 dark:text-lavender-200 leading-relaxed">
            Puoi inserire i dati dei mesi passati dell'anno per ricostruire l'YTD.
            Verranno riportati automaticamente nella relazione del mese successivo come
            precompilati. <span className="font-medium">Celle con bordo ambra</span>: campo
            mancante in una relazione già inviata — puoi completarlo qui, il valore viene
            propagato alla relazione originale.
          </div>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          Riga del mese corrente ({MONTHS_FULL[meseCorrente - 1]}) editabile inline. Mesi passati precompilati
          automaticamente dalle relazioni inviate; mesi futuri in attesa.
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-border/60">
        <table className="w-full text-xs">
          <thead className="bg-accent/40">
            <tr className="text-left">
              {cfgCols.map((c) => <th key={c.key} className="p-2 font-medium">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row) => {
              const rowCls = row.isCurrent
                ? "bg-lavender-500/10"
                : row.isFuture
                ? "opacity-50"
                : row.isPast && row.backfill && !row.hasSibling
                ? "bg-lavender-500/5"
                : "";
              return (
                <tr key={row.mese} className={`border-t border-border/40 ${rowCls}`} data-testid={`tabella-row-${row.mese}`}>
                  {cfgCols.map((c) => (
                    <td key={c.key} className="p-1.5">{renderCell(row, c)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* KPI YTD dinamici: solo colonne con aggregate */}
      {(() => {
        const kpiCols = dataCols.filter((c) => c.aggregate);
        if (kpiCols.length === 0) return null;
        return (
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">
              Obiettivi annuali · YTD fino a {MONTHS_FULL[meseCorrente - 1]}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {kpiCols.map((c) => {
                const agg = aggregateUpTo(c);
                const target = c.target_key_hotel ? targets?.[c.target_key_hotel] : null;
                const suffix = c.aggregate === "avg" ? " media" : " YTD";
                return kpiCard(c.label + suffix, agg, target, true, c.unit || "", c.decimals ?? 2, c.key);
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function StatoBadge({ stato, isFuture, backfill }) {
  if (isFuture) return <span className="text-[10px] text-muted-foreground">futuro</span>;
  if (backfill) {
    return (
      <span
        className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-lavender-50 text-lavender-700 border border-lavender-400 dark:bg-lavender-950/20 dark:text-lavender-300 dark:border-lavender-500/60"
        data-testid="stato-badge-retroattivo"
      >
        retroattivo
      </span>
    );
  }
  const map = {
    compilato: { l: "compilato", cls: "bg-lavender-100 text-lavender-700 dark:bg-lavender-950/30 dark:text-lavender-300" },
    inviata: { l: "inviata", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300" },
    non_compilato: { l: "non compilato", cls: "bg-slate-100 text-slate-600 dark:bg-slate-900/40 dark:text-slate-400" },
  };
  const m = map[stato] || map.non_compilato;
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${m.cls}`}>{m.l}</span>;
}
